// The non-null assertions are acceptable because there definitely will always
// be data in the successful responses where the non-null assertions are made.
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { SearchType } from '@pages/Customers/CustomerSearch/SearchType';
import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {
  BigCommerceResponse,
  Customer,
  CustomerAddress,
  JimboResponse,
  CustomerAttributes,
  CustomerAttributeValues,
  CustomerAttributeUpdate,
  UpdateEmail,
  UpdateResponse,
  BigCommerceEndpointTagTypes,
  FirebaseReset,
} from '@models';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const customerEndpoints = (
  builder: EndpointBuilder<
    // eslint-disable-next-line @typescript-eslint/ban-types
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
    BigCommerceEndpointTagTypes,
    'bigCommerceApi'
  >,
) => {
  return {
    getCustomers: builder.query<BigCommerceResponse<Customer[]>, { searchType: SearchType; searchValue: string; page: number }>({
      query: (query) => {
        if (!query?.searchValue) {
          return `customers?page=${query.page}`;
        }

        return `customers?${query.searchType}=${encodeURIComponent(query.searchValue)}&page=${query.page}`;
      },
      providesTags: ['Customer'],
    }),
    getCustomerAttributes: builder.query<CustomerAttributes[], void>({
      query: () => `customers/attributes`,
      transformResponse: (response: JimboResponse<CustomerAttributes[]>) => response.data!,
      providesTags: ['Customer'],
    }),

    updateCustomerAttributes: builder.mutation<CustomerAttributeUpdate[], CustomerAttributeUpdate>({
      query: (attribute) => ({
        url: 'customers/attribute-values',
        method: 'PUT',
        body: attribute,
      }),
      transformResponse: (response: JimboResponse<CustomerAttributeValues[]>) => response.data!,
      invalidatesTags: ['Customer'],
    }),

    deleteCustomerAttribute: builder.mutation<JimboResponse, CustomerAttributeUpdate>({
      query: (attribute) => ({
        url: `customers/delete-attribute-values`,
        method: 'PUT',
        body: attribute,
      }),
      invalidatesTags: ['Customer'],
    }),

    updateCustomer: builder.mutation<Customer, Customer>({
      query: (customer) => ({
        url: 'customers',
        method: 'PUT',
        body: customer,
      }),
      transformResponse: (response: JimboResponse<Customer>) => response.data!,
      invalidatesTags: ['Customer'],
    }),

    updateCustomerAddress: builder.mutation<CustomerAddress, Partial<CustomerAddress>>({
      query: (address) => ({
        url: 'customers/addresses',
        method: 'PUT',
        body: address,
      }),
      transformResponse: (response: JimboResponse<CustomerAddress>) => response.data!,
      invalidatesTags: ['Customer'],
    }),

    createCustomerAddress: builder.mutation<CustomerAddress, Partial<CustomerAddress>>({
      query: (address) => ({
        url: 'customers/addresses',
        method: 'POST',
        body: address,
      }),
      transformResponse: (response: JimboResponse<CustomerAddress>) => response.data!,
      invalidatesTags: ['Customer'],
    }),

    deleteCustomerAddress: builder.mutation<JimboResponse, number>({
      query: (addressId) => ({
        url: `customers/addresses/${addressId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Customer'],
    }),

    resetPassword: builder.mutation<JimboResponse, Customer>({
      query: (customer) => ({
        url: `customers/reset-password`,
        method: 'POST',
        body: customer,
      }),
    }),

    updateEmail: builder.mutation<JimboResponse<UpdateResponse>, UpdateEmail>({
      query: (updateemail) => ({
        url: `customers/updateemail`,
        method: 'PUT',
        body: updateemail,
      }),
      invalidatesTags: ['Customer'],
    }),
    resetFirebaseId: builder.mutation<JimboResponse, FirebaseReset>({
      query: (firebaseUpdate) => ({
        url: `customers/emergency-email-update`,
        method: 'POST',
        body: firebaseUpdate,
      }),
    }),
    terminateCustomer: builder.mutation<JimboResponse, { customerId: number; firebaseUid?: string | number; reason: string }>({
      query: (terminatePayload) => ({
        url: `customers/terminate-account`,
        method: 'DELETE',
        body: terminatePayload,
      }),
      transformResponse: (response: JimboResponse<any>) => response.data!,
      invalidatesTags: ['Customer'],
    }),
  };
};
